import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import * as XLSX from 'xlsx';
import { AdminHeader } from '../admin-header/admin-header';
import { AdminSidebar } from '../admin-sidebar/admin-sidebar';

@Component({
  selector: 'app-stock-item',
  standalone: true,
  imports: [CommonModule, FormsModule, AdminHeader, AdminSidebar],
  templateUrl: './stock-items.html',
  styleUrls: ['./stock-items.css']
})
export class StockItemComponent implements OnInit {
  stockItems: any[] = [];
  categories: string[] = [];
  newCategoryName = '';
  addingCategory = false;

  newItem = {
    name: '', category: '', unit: '', cost_price: 0, selling_price: 0,
    min_stock_level: 0, quantity: 0, expiry_date: ''
  };

  showStockModal = false;
  editingItem: any = null;
  search = '';
  sortKey = '';
  sortAsc = true;
  page = 1;
  pageSize = 10;
  feedbackMessage = '';
  feedbackType: 'success' | 'error' = 'success';
  baseUrl = 'https://logs.proofile.com.ng/api/stock-items';
  categoryUrl = 'https://logs.proofile.com.ng/api/categories';

  filterCategory = '';
  filterUnit = '';
  filterMin: number | null = null;
  filterQty: number | null = null;
  filterExpiryMonth = '';

  restockModalOpen = false;
  selectedRestockItem: any = null;
  restockAmount: number = 0;
  restockBy: string = 'admin';

  monthList = [
    { name: 'January', value: '01' }, { name: 'February', value: '02' },
    { name: 'March', value: '03' }, { name: 'April', value: '04' },
    { name: 'May', value: '05' }, { name: 'June', value: '06' },
    { name: 'July', value: '07' }, { name: 'August', value: '08' },
    { name: 'September', value: '09' }, { name: 'October', value: '10' },
    { name: 'November', value: '11' }, { name: 'December', value: '12' }
  ];

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    this.loadStockItems();
    this.loadCategories();
  }

  loadStockItems() {
    this.http.get<any[]>(this.baseUrl).subscribe(data => this.stockItems = data);
  }

  loadCategories() {
    this.http.get<any[]>(this.categoryUrl)
      .subscribe(cats => this.categories = cats.map(c => c.name).sort());
  }

  // ✅ FIXED: Accept the full Event, extract value safely
  onCategoryChange(event: Event) {
    const target = event.target as HTMLSelectElement;
    const value = target?.value || '';
    this.addingCategory = value === '__new__';
    if (!this.addingCategory) this.newCategoryName = '';
  }

  async saveItem() {
    if (this.addingCategory && this.newCategoryName.trim()) {
      try {
        const res: any = await this.http.post(this.categoryUrl, { name: this.newCategoryName.trim() }).toPromise();
        this.newItem.category = res.name;
        this.loadCategories();
      } catch (err) {
        return this.showMessage('❌ Failed to create category', 'error');
      }
    }

    const newName = this.newItem.name.trim().toLowerCase();
    if (!this.editingItem) {
      const exists = this.stockItems.find(item => item.name.trim().toLowerCase() === newName);
      if (exists) {
        this.showMessage('❗ This product already exists. Use restock instead.', 'error');
        this.resetForm();
        return;
      }
    }

    const request = this.editingItem
      ? this.http.put(`${this.baseUrl}/${this.editingItem.id}`, this.newItem)
      : this.http.post(this.baseUrl, this.newItem);

    request.subscribe(() => {
      this.loadStockItems();
      this.resetForm();
      this.showMessage('✅ Item saved successfully', 'success');
    }, err => {
      this.showMessage('❌ Failed to save item', 'error');
    });
  }

  edit(item: any) {
    this.newItem = { ...item };
    this.editingItem = item;
    this.showStockModal = true;
  }

  delete(id: number) {
    if (confirm('Are you sure you want to delete this stock item?')) {
      this.http.delete(`${this.baseUrl}/${id}`).subscribe(() => {
        this.loadStockItems();
        this.showMessage('Item deleted', 'success');
      });
    }
  }

  resetForm() {
    this.newItem = {
      name: '', category: '', unit: '', cost_price: 0, selling_price: 0,
      min_stock_level: 0, quantity: 0, expiry_date: ''
    };
    this.newCategoryName = '';
    this.addingCategory = false;
    this.editingItem = null;
    this.showStockModal = false;
  }

  sortBy(key: string) {
    if (this.sortKey === key) this.sortAsc = !this.sortAsc;
    else { this.sortKey = key; this.sortAsc = true; }
  }

  isLowStock(item: any): boolean {
    return item.quantity < item.min_stock_level;
  }

  isNearExpiry(item: any): boolean {
    const expiry = new Date(item.expiry_date);
    const today = new Date();
    const diff = (expiry.getTime() - today.getTime()) / (1000 * 3600 * 24);
    return diff <= 30;
  }

  get uniqueCategories(): string[] {
    return Array.from(new Set(this.stockItems.map(i => i.category))).sort();
  }

  clearFilters() {
    this.search = '';
    this.filterCategory = '';
    this.filterUnit = '';
    this.filterMin = null;
    this.filterQty = null;
    this.filterExpiryMonth = '';
    this.page = 1;
  }

  filteredItems() {
    return this.stockItems.filter(item => {
      const matchName = item.name.toLowerCase().includes(this.search.toLowerCase());
      const matchCategory = !this.filterCategory || item.category === this.filterCategory;
      const matchUnit = !this.filterUnit || item.unit.toLowerCase().includes(this.filterUnit.toLowerCase());
      const matchMin = this.filterMin === null || item.min_stock_level >= this.filterMin;
      const matchQty = this.filterQty === null || item.quantity >= this.filterQty;
      let matchMonth = true;
      if (this.filterExpiryMonth && item.expiry_date) {
        const expiryMonth = new Date(item.expiry_date).toISOString().slice(5, 7);
        matchMonth = expiryMonth === this.filterExpiryMonth;
      }
      return matchName && matchCategory && matchUnit && matchMin && matchQty && matchMonth;
    }).sort((a, b) => {
      if (!this.sortKey) return 0;
      const valA = a[this.sortKey];
      const valB = b[this.sortKey];
      return this.sortAsc ? (valA > valB ? 1 : -1) : (valA < valB ? 1 : -1);
    });
  }

  get paginatedItems() {
    const start = (this.page - 1) * this.pageSize;
    return this.filteredItems().slice(start, start + this.pageSize);
  }

  totalPages() {
    return Math.ceil(this.filteredItems().length / this.pageSize);
  }

  showMessage(msg: string, type: 'success' | 'error') {
    this.feedbackMessage = msg;
    this.feedbackType = type;
    setTimeout(() => (this.feedbackMessage = ''), 3000);
  }

  restockItem() {
    if (!this.selectedRestockItem || this.restockAmount <= 0) return;

    const payload = {
      stock_item_id: this.selectedRestockItem.id,
      added_quantity: this.restockAmount,
      added_by: this.restockBy
    };

    this.http.post('https://logs.proofile.com.ng/api/restock', payload).subscribe({
      next: () => {
        this.loadStockItems();
        this.restockModalOpen = false;
        this.restockAmount = 0;
        this.showMessage('✅ Restocked successfully', 'success');
      },
      error: err => {
        console.error('❌ Restock failed:', err);
        this.showMessage('❌ Restock failed', 'error');
      }
    });
  }

  exportToCSV() {
    const rows = this.stockItems.map(i => ({
      Name: i.name,
      Category: i.category,
      Unit: i.unit,
      Cost: i.cost_price,
      Selling: i.selling_price,
      Min: i.min_stock_level,
      Quantity: i.quantity,
      Expiry: i.expiry_date
    }));

    const csv = [Object.keys(rows[0]).join(','), ...rows.map(r => Object.values(r).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'stock_items.csv';
    a.click();
  }

  importFromExcel(event: any) {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e: any) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json<any>(worksheet, { raw: false });

      const headerMap: { [key: string]: string } = {
        'name': 'name', 'category': 'category', 'unit': 'unit',
        'cost': 'cost_price', 'cost_price': 'cost_price',
        'selling': 'selling_price', 'selling_price': 'selling_price',
        'expiry': 'expiry_date', 'expiry_date': 'expiry_date',
        'min': 'min_stock_level', 'min_stock_level': 'min_stock_level',
        'quantity': 'quantity'
      };

      const mappedItems = json.map(row => {
        const item: any = {};
        for (const key in row) {
          const normalizedKey = key.trim().toLowerCase().replace(/\s+/g, '_');
          const mappedKey = headerMap[normalizedKey];
          if (mappedKey) {
            if (mappedKey === 'expiry_date') {
              const parsed = new Date(row[key]);
              if (!isNaN(parsed.getTime())) {
                const yyyy = parsed.getFullYear();
                const mm = String(parsed.getMonth() + 1).padStart(2, '0');
                const dd = String(parsed.getDate()).padStart(2, '0');
                item.expiry_date = `${yyyy}-${mm}-${dd}`;
              } else item.expiry_date = null;
            } else item[mappedKey] = row[key];
          }
        }

        item.cost_price = parseFloat(item.cost_price || '0');
        item.selling_price = parseFloat(item.selling_price || '0');
        item.min_stock_level = parseInt(item.min_stock_level || '0');
        item.quantity = parseInt(item.quantity || '0');

        return item;
      });

      const filtered = mappedItems.filter(i =>
        i.name && i.category && i.unit &&
        !isNaN(i.cost_price) && !isNaN(i.selling_price) &&
        !isNaN(i.min_stock_level) && !isNaN(i.quantity) &&
        i.expiry_date
      );

      try {
        for (let i = 0; i < filtered.length; i += 200) {
          await this.http.post(`${this.baseUrl}/import`, filtered.slice(i, i + 200)).toPromise();
        }
        this.loadStockItems();
        this.showMessage('📥 Excel import successful!', 'success');
      } catch (error) {
        console.error('❌ Error during chunk upload:', error);
        this.showMessage('❌ Excel import failed.', 'error');
      }
    };

    reader.readAsArrayBuffer(file);
  }
}
